import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';
import { Annotation } from '@/services/annotationService';
import { useAnnotations } from '@/hooks/useAnnotations';
import { wrapTouchEvent } from './touchEventWrapper';

interface ImageAnnotationProps {
  annotation: Annotation;
  isSelected: boolean;
  isHovered: boolean;
  scale: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  isInteractive: boolean;
}

export function ImageAnnotation({
  annotation,
  isSelected,
  isHovered,
  scale,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  isInteractive
}: ImageAnnotationProps) {
  const { data, style } = annotation;
  const x = data.x!;
  const y = data.y!;
  const width = data.width!;
  const height = data.height!;
  const url = data.url!;
  const rotation = data.rotation || 0;
  
  // State declarations
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [rotationAngle, setRotationAngle] = useState(data.rotation || 0);
  const { updateAnnotation } = useAnnotations(annotation.floor_plan_id);
  
  // Update local rotation angle when annotation changes
  useEffect(() => {
    if (!isRotating) {
      setRotationAngle(rotation);
    }
  }, [rotation, isRotating]);
  
  // Reset states when URL changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    
    if (url) {
      // Simple approach: just show the image after a short delay
      // This handles most cases where the image is accessible but takes time to load
      const showTimeout = setTimeout(() => {
        setImageLoaded(true);
      }, 100); // Short delay to allow UI to update
      
      // Also try to preload to detect errors
      const img = new Image();
      img.onload = () => {
        clearTimeout(showTimeout);
        setImageLoaded(true);
      };
      
      img.onerror = () => {
        clearTimeout(showTimeout);
        // Even on error, try to show the image as browsers might still display it
        setImageLoaded(true);
      };
      
      img.src = url;
      
      return () => {
        clearTimeout(showTimeout);
      };
    }
  }, [url]);
  
  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent, handle: string) => {
    if (!isInteractive) return;
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    
    // Get the SVG element once
    const svg = (e.target as SVGElement).ownerSVGElement || (e.target as SVGElement).closest('svg');
    if (!svg) return;
    
    // Get initial mouse position in SVG coordinates
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const initialMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    
    // Store the original values from annotation data
    const originalData = {
      x: annotation.data.x!,
      y: annotation.data.y!,
      width: annotation.data.width!,
      height: annotation.data.height!
    };
    
    // Track the last updated values
    let lastX = originalData.x;
    let lastY = originalData.y;
    let lastWidth = originalData.width;
    let lastHeight = originalData.height;
    
    const handleMove = (clientX: number, clientY: number) => {
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const currentMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());
      
      // Calculate delta from initial mouse position
      const deltaX = currentMousePos.x - initialMousePos.x;
      const deltaY = currentMousePos.y - initialMousePos.y;
      
      let newX = originalData.x;
      let newY = originalData.y;
      let newWidth = originalData.width;
      let newHeight = originalData.height;
      
      // Calculate new dimensions based on handle using deltas
      switch (handle) {
        case 'nw': // top-left
          newX = originalData.x + deltaX;
          newY = originalData.y + deltaY;
          newWidth = originalData.width - deltaX;
          newHeight = originalData.height - deltaY;
          // Ensure minimum size
          if (newWidth < 50) {
            newX = originalData.x + originalData.width - 50;
            newWidth = 50;
          }
          if (newHeight < 50) {
            newY = originalData.y + originalData.height - 50;
            newHeight = 50;
          }
          break;
        case 'ne': // top-right
          newY = originalData.y + deltaY;
          newWidth = originalData.width + deltaX;
          newHeight = originalData.height - deltaY;
          // Ensure minimum size
          if (newWidth < 50) newWidth = 50;
          if (newHeight < 50) {
            newY = originalData.y + originalData.height - 50;
            newHeight = 50;
          }
          break;
        case 'se': // bottom-right
          newWidth = originalData.width + deltaX;
          newHeight = originalData.height + deltaY;
          // Ensure minimum size
          if (newWidth < 50) newWidth = 50;
          if (newHeight < 50) newHeight = 50;
          break;
        case 'sw': // bottom-left
          newX = originalData.x + deltaX;
          newWidth = originalData.width - deltaX;
          newHeight = originalData.height + deltaY;
          // Ensure minimum size
          if (newWidth < 50) {
            newX = originalData.x + originalData.width - 50;
            newWidth = 50;
          }
          if (newHeight < 50) newHeight = 50;
          break;
        case 'n': // top
          newY = originalData.y + deltaY;
          newHeight = originalData.height - deltaY;
          if (newHeight < 50) {
            newY = originalData.y + originalData.height - 50;
            newHeight = 50;
          }
          break;
        case 'e': // right
          newWidth = originalData.width + deltaX;
          if (newWidth < 50) newWidth = 50;
          break;
        case 's': // bottom
          newHeight = originalData.height + deltaY;
          if (newHeight < 50) newHeight = 50;
          break;
        case 'w': // left
          newX = originalData.x + deltaX;
          newWidth = originalData.width - deltaX;
          if (newWidth < 50) {
            newX = originalData.x + originalData.width - 50;
            newWidth = 50;
          }
          break;
      }
      
      // Store the last values
      lastX = newX;
      lastY = newY;
      lastWidth = newWidth;
      lastHeight = newHeight;
      
      // Update annotation with optimistic update
      updateAnnotation({
        id: annotation.id,
        data: {
          data: {
            ...annotation.data,
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight
          }
        },
        skipHistory: true
      });
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      
      // Create final update with history if values changed
      if (lastX !== originalData.x || lastY !== originalData.y || 
          lastWidth !== originalData.width || lastHeight !== originalData.height) {
        updateAnnotation({
          id: annotation.id,
          data: {
            data: {
              ...annotation.data,
              x: lastX,
              y: lastY,
              width: lastWidth,
              height: lastHeight
            }
          },
          skipHistory: false
        });
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    
    const handleTouchEnd = () => {
      handleMouseUp();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [annotation, updateAnnotation, isInteractive]);
  
  // Handle rotation
  const handleRotationStart = useCallback((e: React.MouseEvent) => {
    if (!isInteractive) return;
    e.stopPropagation();
    e.preventDefault();
    setIsRotating(true);
    
    // Get the SVG element
    const svg = (e.target as SVGElement).ownerSVGElement || (e.target as SVGElement).closest('svg');
    if (!svg) return;
    
    // Calculate center of image
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    
    // Get initial angle
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    const initialAngle = Math.atan2(svgP.y - centerY, svgP.x - centerX) * (180 / Math.PI);
    const startRotation = rotationAngle; // Use current rotationAngle, not rotation prop
    
    // Store the latest rotation value
    let latestRotation = startRotation;
    
    const handleMove = (clientX: number, clientY: number, shiftKey: boolean = false) => {
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const currentPos = pt.matrixTransform(svg.getScreenCTM()!.inverse());
      
      // Calculate current angle
      const currentAngle = Math.atan2(currentPos.y - centerY, currentPos.x - centerX) * (180 / Math.PI);
      const deltaAngle = currentAngle - initialAngle;
      let newRotation = (startRotation + deltaAngle) % 360;
      
      // Normalize to 0-360
      if (newRotation < 0) newRotation += 360;
      
      // Snap to 45-degree increments unless Shift is held
      if (!shiftKey) {
        const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
        const snapThreshold = 5; // degrees
        
        for (const snapAngle of snapAngles) {
          if (Math.abs(newRotation - snapAngle) < snapThreshold) {
            newRotation = snapAngle % 360;
            break;
          }
        }
      }
      
      latestRotation = newRotation;
      setRotationAngle(newRotation);
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY, e.shiftKey);
    };
    
    const handleEnd = async () => {
      setIsRotating(false);
      
      // Final update with history using the latest rotation value
      try {
        await updateAnnotation({
          id: annotation.id,
          data: {
            data: {
              ...annotation.data,
              rotation: latestRotation
            }
          },
          skipHistory: false
        });
      } catch (error) {
        logger.error('Failed to save rotation:', error);
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    
    const handleTouchEnd = () => {
      handleEnd();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [annotation.id, annotation.data, updateAnnotation, isInteractive, x, y, width, height, rotationAngle]);
  
  // Calculate center for rotation
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  
  return (
    <g
      data-annotation-id={annotation.id}
      className={isInteractive ? "cursor-pointer" : ""}
      transform={`rotate(${rotationAngle} ${centerX} ${centerY})`}
    >
      {/* Background/placeholder rectangle - only show stroke when selected or hovered */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={imageError ? '#f3f4f6' : 'rgba(255, 255, 255, 0.01)'}
        stroke={isSelected ? '#2563eb' : isHovered ? '#6b7280' : 'transparent'}
        strokeWidth={isSelected || isHovered ? 2 / scale : 0}
        strokeDasharray={isSelected ? `${4 / scale} ${2 / scale}` : undefined}
        pointerEvents={isInteractive ? 'auto' : 'none'}
        onClick={onClick}
        onMouseDown={isResizing ? undefined : onMouseDown}
        onTouchStart={isResizing ? undefined : onTouchStart}
        onTouchEnd={isResizing ? undefined : onTouchEnd}
        onTouchMove={isResizing ? undefined : onTouchMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
      
      {/* Show a subtle loading indicator only for the first 100ms */}
      {!imageLoaded && !imageError && (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="rgba(0, 0, 0, 0.05)"
          stroke="rgba(0, 0, 0, 0.1)"
          strokeWidth={1 / scale}
          pointerEvents="none"
        />
      )}
      
      {/* Image - Try foreignObject first, fallback to SVG image */}
      {url && (
        <>
          {/* Primary method: foreignObject with HTML img */}
          {!imageError && (
            <foreignObject 
              x={x} 
              y={y} 
              width={width} 
              height={height}
              style={{ opacity: style.opacity || 1 }}
            >
              <img
                src={url}
                alt="Annotation"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: imageLoaded ? 'block' : 'none',
                  pointerEvents: 'none'
                }}
              />
            </foreignObject>
          )}
          
          {/* Fallback method: SVG image element */}
          {imageError && (
            <image
              x={x}
              y={y}
              width={width}
              height={height}
              href={url}
              xlinkHref={url}
              opacity={style.opacity || 1}
              preserveAspectRatio="xMidYMid meet"
              pointerEvents="none"
            />
          )}
        </>
      )}
      
      {/* Hover effect - subtle glow */}
      {isHovered && !isSelected && (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="none"
          stroke="#2563eb"
          strokeWidth={Math.max(2 / scale, 1)}
          strokeOpacity={0.2}
          strokeDasharray={`${4 / scale} ${2 / scale}`}
          pointerEvents="none"
        />
      )}
      
      {/* Rotation handle when selected */}
      {isSelected && isInteractive && (
        <>
          {/* Rotation handle - positioned above the image */}
          <g>
            {/* Line from center to rotation handle */}
            <line
              x1={centerX}
              y1={y - 40 / scale}
              x2={centerX}
              y2={y}
              stroke="#2563eb"
              strokeWidth={2 / scale}
              pointerEvents="none"
            />
            
            {/* Invisible larger hit area for rotation handle */}
            <circle
              cx={centerX}
              cy={y - 40 / scale}
              r={16 / scale}
              fill="transparent"
              pointerEvents="all"
              style={{ cursor: 'grab' }}
              onMouseDown={handleRotationStart}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                wrapTouchEvent(handleRotationStart)(e);
              }}
            />
            
            {/* Rotation handle button - using foreignObject to match fixtures/LV products */}
            <foreignObject
              x={centerX - (10 / scale)}
              y={y - 40 / scale - (10 / scale)}
              width={20 / scale}
              height={20 / scale}
              style={{ overflow: 'visible' }}
            >
              <div
                className="bg-white rounded-full shadow-lg border border-blue-400/80 cursor-grab hover:scale-110 hover:border-blue-500 hover:shadow-xl transition-all flex items-center justify-center"
                style={{
                  width: '20px',
                  height: '20px',
                }}
              >
                {/* Rotation icon - elegant curved arrow (exact match to fixtures/LV products) */}
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-blue-500"
                >
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.0902 3 17.7902 4.58073 19.3499 7"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                  />
                  <path
                    d="M17 7L19.5 7L19.5 4.5"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </foreignObject>
          </g>
          
          {/* Angle indicator when rotating */}
          {isRotating && (
            <g>
              {/* Background for angle text */}
              <rect
                x={centerX - 25 / scale}
                y={centerY - 10 / scale}
                width={50 / scale}
                height={20 / scale}
                rx={4 / scale}
                fill="rgba(37, 99, 235, 0.9)"
                pointerEvents="none"
              />
              {/* Angle text */}
              <text
                x={centerX}
                y={centerY + 3 / scale}
                fill="white"
                fontSize={12 / scale}
                fontWeight="600"
                textAnchor="middle"
                pointerEvents="none"
              >
                {Math.round(rotationAngle || rotation)}°
              </text>
            </g>
          )}
        </>
      )}
      
      {/* Resize handles when selected */}
      {isSelected && isInteractive && (
        <>
          {/* Corner resize handles */}
          {[
            { x: x, y: y, handle: 'nw', cursor: 'nwse-resize' },
            { x: x + width, y: y, handle: 'ne', cursor: 'nesw-resize' },
            { x: x + width, y: y + height, handle: 'se', cursor: 'nwse-resize' },
            { x: x, y: y + height, handle: 'sw', cursor: 'nesw-resize' }
          ].map((handle, index) => (
            <rect
              key={index}
              x={handle.x - 4 / scale}
              y={handle.y - 4 / scale}
              width={8 / scale}
              height={8 / scale}
              fill="#2563eb"
              stroke="white"
              strokeWidth={1 / scale}
              style={{ cursor: handle.cursor }}
              pointerEvents="all"
              onMouseDown={(e) => handleResizeStart(e, handle.handle)}
              onTouchStart={(e) => {
                if (e.touches.length === 1) {
                  e.preventDefault();
                  const syntheticEvent = {
                    ...e,
                    clientX: e.touches[0].clientX,
                    clientY: e.touches[0].clientY,
                    preventDefault: () => e.preventDefault(),
                    stopPropagation: () => e.stopPropagation(),
                    target: e.target,
                    currentTarget: e.currentTarget,
                  } as unknown as React.MouseEvent;
                  handleResizeStart(syntheticEvent, handle.handle);
                }
              }}
            />
          ))}
          
          {/* Edge resize handles */}
          {[
            { x: x + width / 2 - 4 / scale, y: y - 4 / scale, width: 8 / scale, height: 8 / scale, handle: 'n', cursor: 'ns-resize' },
            { x: x + width - 4 / scale, y: y + height / 2 - 4 / scale, width: 8 / scale, height: 8 / scale, handle: 'e', cursor: 'ew-resize' },
            { x: x + width / 2 - 4 / scale, y: y + height - 4 / scale, width: 8 / scale, height: 8 / scale, handle: 's', cursor: 'ns-resize' },
            { x: x - 4 / scale, y: y + height / 2 - 4 / scale, width: 8 / scale, height: 8 / scale, handle: 'w', cursor: 'ew-resize' }
          ].map((handle, index) => (
            <rect
              key={`edge-${index}`}
              x={handle.x}
              y={handle.y}
              width={handle.width}
              height={handle.height}
              fill="#2563eb"
              stroke="white"
              strokeWidth={1 / scale}
              style={{ cursor: handle.cursor }}
              pointerEvents="all"
              onMouseDown={(e) => handleResizeStart(e, handle.handle)}
              onTouchStart={(e) => {
                if (e.touches.length === 1) {
                  e.preventDefault();
                  const syntheticEvent = {
                    ...e,
                    clientX: e.touches[0].clientX,
                    clientY: e.touches[0].clientY,
                    preventDefault: () => e.preventDefault(),
                    stopPropagation: () => e.stopPropagation(),
                    target: e.target,
                    currentTarget: e.currentTarget,
                  } as unknown as React.MouseEvent;
                  handleResizeStart(syntheticEvent, handle.handle);
                }
              }}
            />
          ))}
        </>
      )}
    </g>
  );
}
